import { TEX } from "../../core/keys"
import { LANE_TOP_Y } from "../../data/constants"
import type { EnemyData, GameSprite } from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"

const MINE_DRONE_DROP_MS = 260
const MINE_DRONE_DROP_VISUAL_OFFSET_Y = 12

type MinePhase = "move" | "drop" | "exit"

export class MineDroneEnemy extends StatefulEnemyRuntime<MinePhase> {
  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<MinePhase>[] = [
      {
        name: "move",
        behaviorState: "flying",
        animationState: "flying",
        enter: (ctx) => {
          ctx.sprite.setTexture(TEX.ENEMY_MINE_DRONE_CLOSE)
          ctx.sprite.y = LANE_TOP_Y
        },
        nextStateOnFire: "drop",
        update: (context) => this.updateMove(context),
      },
      {
        name: "drop",
        behaviorState: "flying",
        animationState: "flying",
        canFire: false,
        enter: (ctx) => {
          ctx.sprite.setTexture(TEX.ENEMY_MINE_DRONE_OPEN)
          ctx.sprite.y = LANE_TOP_Y + MINE_DRONE_DROP_VISUAL_OFFSET_Y
        },
        update: (context) => this.updateDrop(context),
      },
      {
        name: "exit",
        behaviorState: "flying",
        animationState: "flying",
        canFire: false,
        enter: (ctx) => {
          ctx.sprite.setTexture(TEX.ENEMY_MINE_DRONE_CLOSE)
          ctx.sprite.y = LANE_TOP_Y
        },
        update: (context) => this.updateExit(context),
      },
    ]

    super(sprite, enemyData, "move", states)
  }

  private getMineSpeed(context: EnemyStateContext<MinePhase>): number {
    const spec = context.enemyDef?.movementSpec
    if (spec?.action !== "mine-climb") return 2.5

    return Math.max(1.2, Math.abs(spec.retreatSpeed))
  }

  private updateMove(context: EnemyStateContext<MinePhase>): MinePhase | null {
    const { sprite, enemyData, scrollCompensation } = context
    const spec = context.enemyDef?.movementSpec
    if (spec?.action !== "mine-climb") return null

    const leftSpeed = this.getMineSpeed(context)
    enemyData.vx = scrollCompensation - leftSpeed
    enemyData.vy = 0
    sprite.y = LANE_TOP_Y

    if (sprite.x <= 60) {
      return "exit"
    }

    return null
  }

  private updateDrop(context: EnemyStateContext<MinePhase>): MinePhase | null {
    const { enemyData, scrollCompensation, stateTime } = context
    const spec = context.enemyDef?.movementSpec
    if (spec?.action !== "mine-climb") return null

    const leftSpeed = this.getMineSpeed(context)
    enemyData.vx = scrollCompensation - leftSpeed
    enemyData.vy = 0
    context.sprite.y = LANE_TOP_Y + MINE_DRONE_DROP_VISUAL_OFFSET_Y

    if (stateTime >= MINE_DRONE_DROP_MS) {
      return "move"
    }
    return null
  }

  private updateExit(context: EnemyStateContext<MinePhase>): MinePhase | null {
    const { enemyData, scrollCompensation } = context
    const spec = context.enemyDef?.movementSpec
    if (spec?.action !== "mine-climb") return null

    enemyData.vx = scrollCompensation - this.getMineSpeed(context)
    enemyData.vy = 0
    context.sprite.y = LANE_TOP_Y
    return null
  }

  public override onFire(): void {
    if (this.getStateName() === "move") {
      this.transitionTo("drop")
      return
    }

    super.onFire()
  }
}
